import type { FunctionalComponent } from 'preact';
import { useState } from 'preact/hooks';
import { route } from 'preact-router';
import { SafeImage } from '@/components/safe-image.tsx';
import { Header } from '@/components/header.tsx';
import { TabBar } from '@/components/tab-bar.tsx';
import { Pager } from '@/components/pager.tsx';
import { WebzineFooter } from '@/components/webzine/webzine-footer.tsx';
import { LoadingSpinner } from '@/components/loading-spinner.tsx';
import { useWebzineIssues } from '@/hooks/use-webzine.ts';
import { formatIssueDate, issueHref } from '@/types/webzine.ts';
import type { PageProps } from '@/types/props.ts';

/** 시안 `webzine-list.html` 의 `pageSize`. 호가 늘면 여기만 바꾸면 된다. */
const PAGE_SIZE = 2;

/** 페이지네이션 화살표 (시안 인라인 SVG) */
/**
 * 웹진 목록 (시안 `webzine-list.html`).
 *
 * 목록은 `MC_WEBZINE` 에서 온다. 호가 많아지면 지금의 화면 안 페이지네이션을
 * 서버 페이징(`items.json` 의 `p`)으로 바꾸면 된다.
 *
 * 시안의 카테고리 필터(`.wz-list-filter`)는 옮기지 않았다. 마크업이 통째로 주석
 * 처리돼 있고 `data-category` 도 편집 데이터에 없다.
 */
const WebzineList: FunctionalComponent<PageProps> = () => {
  const [page, setPage] = useState(1);
  const { issues, latest, loading } = useWebzineIssues();

  const totalPages = Math.max(1, Math.ceil(issues.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const visible = issues.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const latestSlug = latest?.slug ?? null;

  function go(e: MouseEvent, href: string) {
    e.preventDefault();
    route(href);
  }

  return (
    <div class="page">
      <Header />

      {/* 헤더 · 탭바만 고정, 이 안쪽만 스크롤 */}
      <div class="section">
        <div class="contents">
          <main class="webzine">
            <section class="wz-list-head">
              <div class="section-inner">
                <h1 class="wz-list-title">Webzine</h1>
              </div>
            </section>

            <section class="wz-list-body">
              <div class="section-inner">
                {loading && <LoadingSpinner show />}

                <div class="post-grid">
                  {visible.map((issue) => (
                    <a
                      key={issue.slug}
                      href={issueHref(issue, latestSlug)}
                      class="post-card"
                      onClick={(e) => go(e, issueHref(issue, latestSlug))}
                    >
                      <span class="post-thumb">
                        <SafeImage src={issue.card.thumbnail} alt={issue.card.title} />
                      </span>
                      <span class="post-tag">{issue.vol}</span>
                      <span class="post-title">{issue.card.title}</span>
                      <span class="post-desc">{issue.card.description}</span>
                      <span class="post-meta">
                        <span class="post-date">{formatIssueDate(issue.date)}</span>
                      </span>
                    </a>
                  ))}
                </div>

                {!loading && visible.length === 0 && (
                  // 시안도 전용 스타일 없이 인라인으로만 꾸며져 있다.
                  <p
                    class="wz-list-empty"
                    style={{ textAlign: 'center', color: '#a3a3a3', padding: '3rem 0' }}
                  >
                    아직 준비 중인 콘텐츠예요.
                  </p>
                )}

                <Pager page={current} totalPages={totalPages} onChange={setPage} />
              </div>
            </section>
          </main>

          <WebzineFooter />
        </div>
      </div>

      <TabBar path="/webzine" />
    </div>
  );
};

export default WebzineList;
